import classNames from 'classnames';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';

import { AppDispatch } from 'store/store';
import UDTabs from 'ud-ui/components/tabs';
import { Tab } from 'ud-ui/components/tabs/interfaces';
import UDText from 'ud-ui/components/text';
import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import {
  selectSymbolicTeam,
  showChampionshipStageForms,
  showChampionshipSymbolicTeamForm,
} from 'modules/championship/store/championship/actions';
import { ChampionshipStateData } from 'modules/championship/store/championship/index';
import { selectedStageSelector, stagesActions } from 'modules/championship/store/stages';
import { getChampionshipEditTabs } from './constants';

type CreateEditFormParams = {
  tab: Tab;
  count?: number;
  isActive?: boolean;
  subLabel?: string;
  action?: () => void;
  disabled?: boolean;
};

const createEditFormTab = (params: CreateEditFormParams): Tab => {
  const { tab, count, isActive, subLabel, action, disabled } = params;

  if (count !== undefined) {
    const badgeBgColor = isActive ? 'bg-surfaceWhite' : 'bg-accentNaplesYellow';
    const className = classNames([
      'text-surfaceRaisinBlack50',
      badgeBgColor,
      'p-1',
      'rounded-[4px]',
      'ml-2',
      'line-height-1',
      'caption-1',
    ]);

    return {
      hash: tab.hash,
      disabled,
      label: (
        <>
          {tab.label}{' '}
          <UDText type="subhead" className={className}>
            {count}
          </UDText>
        </>
      ),
    };
  }

  return { ...tab, subLabel, onClick: action, disabled };
};

type ChampionshipEditFormTabsProps = {
  championship?: ChampionshipStateData;
};

const ChampionshipEditFormTabs = ({ championship }: ChampionshipEditFormTabsProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const location = useLocation();
  const activeTab = (location.hash || '').split('/')[0];
  const isTeam = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/:teamId`);
  const isStage = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/steps`);
  const isSymbolicTeams = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/symbolic-teams`);
  const stage = useSelector(selectedStageSelector);

  let subLabel: string | undefined;
  let urlTab: string | undefined;
  let action = () => {};

  if (isTeam) {
    urlTab = 'applications';
    subLabel = championship?.teams?.find((t) => t.id === Number(isTeam.params.teamId))?.name;
  }

  if (isStage) {
    urlTab = 'steps';
    subLabel = stage?.name || (championship?.showStageForms ? t('championship.editTabs.addStageSubLabel') : undefined);
    action = () => {
      if (!championship) {
        return;
      }

      dispatch(stagesActions.stageSelected(null));
      dispatch(showChampionshipStageForms({ state: false, championshipId: championship.id }));
    };
  }

  if (isSymbolicTeams && championship?.showSymbolicTeamForm) {
    urlTab = ChampionshipEditTab.SYMBOLIC_TEAMS;
    subLabel = championship.selectedSymbolicTeam?.name || t('championship.editTabs.addSymbolicTeamSubLabel');
    action = () => {
      if (!championship) {
        return;
      }

      dispatch(selectSymbolicTeam({ symbolicTeam: null, championshipId: championship.id }));
      dispatch(showChampionshipSymbolicTeamForm({ state: false, championshipId: championship.id }));
    };
  }

  const tabs = useMemo(() => {
    return getChampionshipEditTabs(t).map((tab) =>
      createEditFormTab({
        tab: tab,
        isActive: tab.hash === activeTab,
        subLabel: urlTab === tab.hash ? subLabel : undefined,
        action: urlTab === tab.hash ? action : undefined,
        disabled: !championship,
      }),
    );
  }, [t, activeTab, urlTab, subLabel, championship]);

  return <UDTabs tabs={tabs} defaultTab={tabs[0]} activeRoutePattern={Pages.CHAMPIONSHIPS.EDIT} />;
};

export default ChampionshipEditFormTabs;
